import { useEffect, useState } from "react";
import { Target, Loader2, Crosshair } from "lucide-react";
import api from "@/lib/api";
import { AccuracyBreakdown } from "@/components/AccuracyBreakdown";

const OUTCOME = {
  target_hit: { label: "Target hit", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  stop_hit: { label: "Stop hit", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  expired: { label: "Expired", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  cancelled: { label: "Cancelled", cls: "text-slate-400 bg-white/5 border-white/10" },
  not_triggered: { label: "Not triggered", cls: "text-slate-400 bg-white/5 border-white/10" },
};

const Stat = ({ title, s, testId }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{title}</p>
    {s?.total ? (
      <>
        <p className="font-mono-num text-3xl font-extrabold text-emerald-400 mt-1" data-testid={`${testId}-hit-rate`}>{s.hit_rate}%</p>
        <p className="text-xs text-slate-400">of last {s.total} resolved signals hit target</p>
        <div className="flex h-1.5 rounded-full overflow-hidden bg-white/5 mt-2">
          <div style={{ width: `${(s.target_hit / s.total) * 100}%` }} className="bg-emerald-500" />
          <div style={{ width: `${(s.stop_hit / s.total) * 100}%` }} className="bg-red-500" />
          <div style={{ width: `${(s.expired / s.total) * 100}%` }} className="bg-amber-500" />
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5 font-mono-num">{s.target_hit} target · {s.stop_hit} stop · {s.expired} expired{s.avg_predicted != null && <> · predicted avg {s.avg_predicted}%</>}</p>
        {s.by_source?.backtest && s.by_source?.ai_estimate && (
          <p className="text-[11px] text-slate-500 font-mono-num">backtested {s.by_source.backtest.hit_rate}% ({s.by_source.backtest.total}) · AI estimate {s.by_source.ai_estimate.hit_rate}% ({s.by_source.ai_estimate.total})</p>
        )}
      </>
    ) : (
      <p className="text-sm text-slate-400 mt-2" data-testid={`${testId}-empty`}>No resolved signals yet{s?.pending ? ` · ${s.pending} being tracked` : ""}.</p>
    )}
  </div>
);

export const SignalAccuracy = () => {
  const [acc, setAcc] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/signals/accuracy").then(({ data }) => setAcc(data)).catch(() => setAcc(false));
    api.get("/signals/recent?limit=6").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);
  if (acc === false) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="signal-accuracy">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="font-semibold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Live Signal Accuracy</p>
          <p className="text-xs text-slate-400">Every AI signal is saved with entry, stop and target, then checked against real candles every {acc?.checked_every_minutes || 15} min.</p>
        </div>
      </div>
      {!acc ? <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p> : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Stat title="Your last 100 signals" s={acc.mine} testId="accuracy-mine" />
            <Stat title="Platform-wide last 100 signals" s={acc.platform} testId="accuracy-platform" />
          </div>
          <AccuracyBreakdown breakdown={acc.breakdown?.platform} />
        </>
      )}
      {recent.length > 0 && (
        <ul className="mt-4 divide-y divide-white/5" data-testid="recent-signals">
          {recent.map((s) => {
            const o = OUTCOME[s.outcome] || { label: s.status === "triggered" ? "Tracking · entry filled" : "Waiting for entry", cls: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30" };
            return (
              <li key={s.signal_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`recent-signal-${s.signal_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Crosshair className={`w-4 h-4 shrink-0 ${s.side === "BUY" ? "text-emerald-400" : "text-red-400"}`} />
                  <span className="font-mono-num font-semibold">{s.symbol}</span>
                  <span className={`text-xs font-semibold ${s.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>{s.side}</span>
                  <span className="text-xs text-slate-500 font-mono-num hidden sm:inline">{s.entry} → {s.target} / {s.stop}</span>
                  <span className="text-[11px] text-slate-500">{s.probability}% {String(s.probability_source).startsWith("backtest") ? "backtested" : "AI est."}</span>
                </div>
                <span className={`text-[11px] border rounded-full px-2 py-0.5 whitespace-nowrap ${o.cls}`}>{o.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
